import { useTranslation } from 'react-i18next';
import { ASPECT_RATIOS, type AspectPresetId, type Size } from '@/core/geometry/framing';
import { checkLength, placementFrame, type Placement } from '@/core/project/placement';
import { formatLength } from '@/core/time/format';

/** How a length check reads, and how loudly. */
export interface LengthMessage {
  readonly tone: 'fits' | 'warning' | 'problem';
  readonly text: string;
}

/**
 * A placement in the person's language: its name, its facts as short chips,
 * and what its length check says about an edit. One place for these words so
 * the home page, the Format tab and the save window never disagree.
 */
export function usePlacementText() {
  const { t, i18n } = useTranslation();
  const language = i18n.language;
  const length = (seconds: number) => formatLength(seconds, t('placement.units', { returnObjects: true }), language);
  const name = (placement: Placement) => t(`placement.names.${placement.id}`);
  const shape = (aspect: AspectPresetId) => (aspect === 'original' ? t('placement.asFilmed') : aspect);

  return {
    name,
    short: (placement: Placement) => t(`placement.short.${placement.id}`),
    shape,
    /** Shape, length and size in a few words each ("9:16", "Up to 15 min", "Under 4 GB"). */
    facts(placement: Placement, source?: Size): { readonly key: string; readonly text: string; readonly aspect?: number }[] {
      const frame = source ? placementFrame(placement, source) : placement.frame;
      const aspect = frame === 'original' ? (source ? source.width / source.height : undefined) : ASPECT_RATIOS[frame];
      return [
        { key: 'shape', text: shape(frame), ...(aspect && { aspect }) },
        ...(placement.minLengthSec !== undefined ? [{ key: 'min', text: t('placement.from', { length: length(placement.minLengthSec) }) }] : []),
        ...(placement.maxLengthSec !== undefined ? [{ key: 'max', text: t('placement.upTo', { length: length(placement.maxLengthSec) }) }] : []),
        // The platform's own unit and figure ("4 GB"), not the app's MB.
        ...(placement.maxSize ? [{ key: 'size', text: t('placement.under', { size: `${placement.maxSize.value.toLocaleString(language)} ${placement.maxSize.unit}` }) }] : []),
      ];
    },
    /** What the placement says about an edit this long. */
    lengthMessage(placement: Placement, durationSec: number): LengthMessage {
      const place = name(placement);
      const check = checkLength(placement, durationSec);
      switch (check.kind) {
        case 'fits':
          return { tone: 'fits', text: t('placement.length.fits', { place, length: length(durationSec) }) };
        case 'short':
          return { tone: 'problem', text: t('placement.length.short', { place, min: length(check.minSec), length: length(durationSec) }) };
        case 'long':
          return { tone: 'problem', text: t('placement.length.long', { place, max: length(check.maxSec), length: length(durationSec), over: length(Math.ceil(durationSec - check.maxSec)) }) };
        case 'longer-for':
          return { tone: 'warning', text: t(`placement.length.${check.who}`, { place, max: length(check.maxSec) }) };
      }
    },
  };
}
